import type { B2BProductVariant, CollectionProduct } from '~/types'

/** One ticked purchase unit, in the shape `cart/add-many` wants it. */
export type CatalogSelectionEntry = {
  itemId: string
  minQty: number
  amount: number
}

/**
 * What the manager toolbar has ticked in the catalogue.
 *
 * Keyed by `B2BProductVariant.id` (`{itemId}-{minQty}`), the same key the cart
 * uses, because the unit of selection is a *purchase unit* and not a product: a
 * box of ten and a single piece of the same SKU are separate rows with separate
 * prices, and a manager routinely wants one without the other.
 *
 * The selection deliberately outlives a page change — a manager filters, ticks a
 * few rows, pages on and ticks more, and only then presses "Tęsti". It is cleared
 * when that add succeeds, and on sign-out; nothing else touches it. It is not
 * persisted, so a reload starts clean rather than resurrecting a selection whose
 * prices or stock may have moved on.
 */
export const useCatalogSelection = defineStore('catalogSelection', () => {
  const entries = ref(new Map<string, CatalogSelectionEntry>())

  const count = computed(() => entries.value.size)
  const items = computed<CatalogSelectionEntry[]>(() => [...entries.value.values()])

  function isSelected(variantId: string): boolean {
    return entries.value.has(variantId)
  }

  function select(variant: B2BProductVariant, amount = 1) {
    entries.value.set(variant.id, {
      itemId: variant.itemId,
      minQty: variant.qty,
      amount: Math.max(1, Math.trunc(amount)),
    })
  }

  function deselect(variantId: string) {
    entries.value.delete(variantId)
  }

  function toggle(variant: B2BProductVariant, amount = 1) {
    if (isSelected(variant.id)) deselect(variant.id)
    else select(variant, amount)
  }

  /**
   * Follows the row's own quantity stepper. A no-op while the row is unticked, so
   * nudging the stepper never selects anything by itself — the checkbox does that.
   */
  function setAmount(variantId: string, amount: number) {
    const entry = entries.value.get(variantId)
    if (!entry) return
    entries.value.set(variantId, { ...entry, amount: Math.max(1, Math.trunc(amount)) })
  }

  /**
   * "Pažymėti viską" over the rows currently on screen.
   *
   * One tier per product, not every tier — the same rule the Smarty catalogue's
   * select-all follows (`listing.tpl`): ticking all six units of a product would
   * put six overlapping lines of the same SKU in the cart, which is never what
   * the button means. A product that already has one of its units ticked is left
   * exactly as the manager set it.
   *
   * The tier it ticks is the first one that can actually be bought. `cart/add-many`
   * refuses an out-of-stock unit, so ticking the first tier blind meant a row whose
   * cheapest unit had run out went into the batch only to come back in `failed` —
   * and the row's own tick is disabled for that unit anyway, so the button would
   * have been setting something the manager could not have set by hand.
   */
  function selectAll(products: CollectionProduct[]) {
    for (const product of products) {
      const first = product.variants.find(variant => variant.inStock)
      if (!first) continue
      if (product.variants.some(variant => isSelected(variant.id))) continue
      select(first)
    }
  }

  /** Unticks only what is on screen, leaving a selection made on another page. */
  function deselectAll(products: CollectionProduct[]) {
    for (const product of products) {
      for (const variant of product.variants) deselect(variant.id)
    }
  }

  function clear() {
    entries.value.clear()
  }

  return { entries, items, count, isSelected, select, deselect, toggle, setAmount, selectAll, deselectAll, clear }
})
